import Typography from '@mui/material/Typography';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import EditionCompetitionToolbar from 'src/common/components/dashboard/EditionCompetitionToolbar';
import DashboardContentPageLayout from 'src/common/components/dashboard/DashboardContentPageLayout';
import EditionYearFilter from 'src/common/components/dashboard/EditionYearFilter';
import EditionInsightsDetail from 'src/common/components/insights/EditionInsightsDetail';
import EditionInsightsHub from 'src/common/components/insights/EditionInsightsHub';
import { LanguageKeys } from 'src/common/constants/languages';
import { useEditionCompetitionQueryParams } from 'src/hooks/useEditionCompetitionQueryParams';

export default function InsightsPage() {
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation();
  const currentLang = resolvedLanguage as LanguageKeys;

  const {
    selectedYear,
    selectedCompetitionName,
    selectedEdition,
    competition,
    competitionsForEdition,
    distances,
    setYear,
    setCompetition,
  } = useEditionCompetitionQueryParams();

  const getCompetitionLink = useCallback(
    (competitionName: string, year: number) =>
      `/dashboard/insights?year=${year}&competition=${competitionName}`,
    [],
  );

  const editionsToShow = useMemo(
    () => (selectedEdition ? [selectedEdition] : []),
    [selectedEdition],
  );

  const competitionTitle = competition?.i18n[currentLang];
  const isDetailView = Boolean(competition && selectedCompetitionName && selectedEdition);

  const hubBackHref =
    selectedYear != null ? `/dashboard/insights?year=${selectedYear}` : '/dashboard/insights';

  const toolbar = isDetailView ? (
    <EditionCompetitionToolbar
      selectedYear={selectedYear}
      selectedCompetitionName={selectedCompetitionName}
      competitionsForEdition={competitionsForEdition}
      currentLang={currentLang}
      onYearChange={setYear}
      onCompetitionChange={setCompetition}
    />
  ) : (
    <EditionYearFilter selectedYear={selectedYear} onYearChange={setYear} />
  );

  return (
    <DashboardContentPageLayout
      title={isDetailView ? competitionTitle : undefined}
      toolbar={toolbar}
      backLink={
        isDetailView
          ? { label: t('insights.backToHub'), href: hubBackHref }
          : undefined
      }
    >
      {isDetailView && competition && selectedEdition ? (
        <EditionInsightsDetail
          edition={selectedEdition}
          competition={competition}
          distances={distances}
          currentLang={currentLang}
          showStatisticsBody
        />
      ) : editionsToShow.length === 0 ? (
        <Typography variant="body1" sx={{ my: 2 }}>
          {t('insights.selectEdition')}
        </Typography>
      ) : (
        editionsToShow.map(edition => (
          <EditionInsightsHub
            key={edition.id}
            edition={edition}
            currentLang={currentLang}
            getCompetitionLink={getCompetitionLink}
            showYearHeading={editionsToShow.length > 1}
          />
        ))
      )}
    </DashboardContentPageLayout>
  );
}
